import {
  DashboardMapRegionOverlay,
  attachOutsideMask,
} from "@/components/dashboard/dashboard-map-region-overlay";
import {
  LocalClusteredMarkers,
  MapClusterSummary,
  type MapClusterSummaryState,
  ServerClusteredMarkers,
} from "@/components/map/map-count-cluster";
import {
  MAP_ZOOM_GESTURE_HANDLING,
  MapZoomHint,
} from "@/components/map/map-zoom-hint";
import {
  StrPropertyPinCard,
  strStatusBadgeVariant,
  strStatusDisplayLabel,
} from "@/components/non_registrant/str-property-pin-card";
import { RegistrationStatusInfoButton } from "@/components/registration-status-info-panel";
import { Badge } from "@/components/ui/badge";
import type { LicenseStatus } from "@/hera-api/client/types.gen";
import {
  listJurisdictionRegions,
  regionLatLngRings,
} from "@/hera-api/regions-api";
import { useStreetViewHeading } from "@/hooks/use-street-view-heading";
import { polygonGroupsFromGeometry } from "@/lib/dashboard-map-region-overlay";
import type { MapCluster, MapViewport } from "@/lib/map-clusters";
import {
  buildPropertyStreetViewImageUrl,
  buildPropertyStreetViewPanoLink,
} from "@/lib/street-view-heading";
import { cn, getPinColor, getRegistrationStatusDisplay } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
  AdvancedMarker,
  Map as GoogleMap,
  InfoWindow,
  Pin,
  useMap,
} from "@vis.gl/react-google-maps";
import * as React from "react";

/** Slim map marker: coordinates and hover fields only. */
export type DashboardMapPin = {
  id: string;
  lat: number;
  lng: number;
  display_id: string;
  address: string;
  status: LicenseStatus;
  renewal_date?: string | null;
  /** When set, drawn instead of the registration-status color. */
  color?: string;
  /** STR property hover card. License and listing maps omit this. */
  strCard?: {
    statusLabel: string;
    platforms: string[];
    heroImageUrl: string | null;
    addressUnverified?: boolean;
  };
};

export type DashboardMapInfoCard = "registration" | "str-property";

interface DashboardMapProps {
  pins: DashboardMapPin[];
  /** Server-built clusters. Omit to cluster `pins` in the browser. */
  clusters?: MapCluster[];
  totalInView?: number;
  onPinClick: (licenseId: string) => void;
  /** When set, the map reports its viewport and renders `pins`/`clusters` as given. */
  onViewportChange?: (viewport: MapViewport) => void;
  /** While true, keep the current camera instead of framing a partial pin set. */
  isLoading?: boolean;
  className?: string;
  /** Registration status flow (i) — operator/government only; hide on registrant dashboard. */
  showRegistrationStatusFlowInfo?: boolean;
  regionJurisdictionIds?: string[];
  selectedRegionIds?: string[];
  /**
   * License and unregistered maps. Draws the jurisdiction outline, or the
   * selected regions, and toggles the region filter from the map.
   */
  regionOverlay?: {
    selectedJurisdictionIds: readonly string[];
    portalJurisdictionIds: readonly string[];
    onRegionFilterChange: (regionIds: string[]) => void;
  };
  /**
   * Registration maps keep Street View. STR property maps show listing hero,
   * platform marks, and the current short-term-rental status.
   */
  infoCard?: DashboardMapInfoCard;
}

// Custom marker component that follows the vis.gl pattern
interface LicenseMarkerProps {
  pin: DashboardMapPin;
  onPinClick: (licenseId: string) => void;
  showRegistrationStatusFlowInfo: boolean;
  infoCard: DashboardMapInfoCard;
}

function LicenseMarker({
  pin,
  onPinClick,
  showRegistrationStatusFlowInfo,
  infoCard,
}: LicenseMarkerProps) {
  const [showInfoWindow, setShowInfoWindow] = React.useState(false);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastMousePositionRef = React.useRef<{ x: number; y: number } | null>(
    null,
  );
  const isHoveringInfoWindowRef = React.useRef(false);
  const justOpenedRef = React.useRef(false);

  const handleClick = React.useCallback(() => {
    // Exit fullscreen mode if active (Google Maps fullscreen)
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {
        // Ignore errors if exitFullscreen fails
      });
    }
    // Call the pin click handler
    onPinClick(pin.id);
  }, [onPinClick, pin.id]);

  const handleMouseEnter = React.useCallback(() => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Store initial mouse position
    lastMousePositionRef.current = { x: 0, y: 0 };

    // Set timeout to show popup after 1 second (half of original 2 seconds)
    hoverTimeoutRef.current = setTimeout(() => {
      justOpenedRef.current = true;
      setShowInfoWindow(true);
      // Reset the flag after a short delay to allow map to settle
      setTimeout(() => {
        justOpenedRef.current = false;
      }, 500);
    }, 1000);
  }, []);

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    const currentPos = { x: e.clientX, y: e.clientY };

    // If mouse moved significantly, reset the timer
    if (lastMousePositionRef.current) {
      const dx = Math.abs(currentPos.x - lastMousePositionRef.current.x);
      const dy = Math.abs(currentPos.y - lastMousePositionRef.current.y);

      // If moved more than 5 pixels, reset
      if (dx > 5 || dy > 5) {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
        lastMousePositionRef.current = currentPos;
        hoverTimeoutRef.current = setTimeout(() => {
          setShowInfoWindow(true);
        }, 1000);
      }
    } else {
      lastMousePositionRef.current = currentPos;
    }
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    // Clear hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    lastMousePositionRef.current = null;

    // Don't hide immediately if we just opened (map might be panning)
    if (justOpenedRef.current) {
      return;
    }

    // Only hide if we're not hovering over the InfoWindow
    // Add a delay to allow moving from marker to InfoWindow
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringInfoWindowRef.current && !justOpenedRef.current) {
        setShowInfoWindow(false);
      }
    }, 200);
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const ref = React.useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement | null) => {
      if (!marker) return;
      const element = marker.element as HTMLElement | null;
      if (!element) return;
      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mousemove", handleMouseMove as EventListener);
      element.addEventListener("mouseleave", handleMouseLeave);
    },
    [handleMouseEnter, handleMouseMove, handleMouseLeave],
  );

  const fullAddress = pin.address?.trim() ? pin.address.trim() : null;
  const strPropertyCard = infoCard === "str-property";

  // Heading from the panorama camera toward this property, not a default of 0.
  const streetViewAim = useStreetViewHeading(
    strPropertyCard || !showInfoWindow ? null : fullAddress,
    strPropertyCard || !showInfoWindow ? null : pin.lat,
    strPropertyCard || !showInfoWindow ? null : pin.lng,
    !strPropertyCard && showInfoWindow,
  );

  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const streetViewImageUrl =
    strPropertyCard || streetViewAim.isLoading
      ? null
      : buildPropertyStreetViewImageUrl({
          apiKey: googleMapsApiKey,
          propertyLatitude: pin.lat,
          propertyLongitude: pin.lng,
          cameraLatitude: streetViewAim.cameraLatitude,
          cameraLongitude: streetViewAim.cameraLongitude,
          panoId: streetViewAim.panoId,
          address: fullAddress,
        });

  const streetViewFullUrl = strPropertyCard
    ? null
    : buildPropertyStreetViewPanoLink({
        propertyLatitude: pin.lat,
        propertyLongitude: pin.lng,
        cameraLatitude: streetViewAim.cameraLatitude,
        cameraLongitude: streetViewAim.cameraLongitude,
        address: fullAddress,
      });

  const strStatusLabel = strPropertyCard
    ? (pin.strCard?.statusLabel ?? "Unregistered")
    : null;
  const statusInfo = strStatusLabel
    ? {
        label: strStatusDisplayLabel(strStatusLabel),
        variant: strStatusBadgeVariant(strStatusLabel),
      }
    : getRegistrationStatusDisplay(pin.status, pin.renewal_date ?? null);

  return (
    <>
      <AdvancedMarker
        ref={ref}
        position={{
          lat: pin.lat,
          lng: pin.lng,
        }}
        onClick={handleClick}
        title={`${pin.display_id} - ${statusInfo.label}`}
      >
        <Pin
          background={
            pin.color ?? getPinColor(pin.status, pin.renewal_date ?? null)
          }
          borderColor="#fff"
          glyphColor="#fff"
        />
      </AdvancedMarker>
      {showInfoWindow && (
        <InfoWindow
          position={{
            lat: pin.lat,
            lng: pin.lng,
          }}
          onCloseClick={() => setShowInfoWindow(false)}
          disableAutoPan={false}
        >
          <style>{`
            .gm-style .gm-style-iw-c {
              padding: 0 !important;
              max-width: 450px !important;
            }
            .gm-style .gm-style-iw-d {
              overflow: visible !important;
              max-width: 450px !important;
            }
            .gm-style .gm-style-iw-t::after {
              display: none !important;
            }
            .gm-style button[title="Close"] {
              display: none !important;
            }
            /* Hide Street View compass and controls */
            iframe[src*="streetview"] ~ * .gm-compass,
            iframe[src*="streetview"] ~ * [title*="compass"],
            iframe[src*="streetview"] ~ * .gmnoprint {
              display: none !important;
            }
          `}</style>
          <button
            type="button"
            className="p-2 cursor-pointer w-full text-left"
            style={{ minWidth: "300px", maxWidth: "450px", width: "auto" }}
            onClick={handleClick}
            onMouseEnter={() => {
              // Keep popup open when hovering over InfoWindow
              isHoveringInfoWindowRef.current = true;
              justOpenedRef.current = false; // Clear the just-opened flag
              if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
              }
            }}
            onMouseLeave={() => {
              // Hide popup when leaving InfoWindow
              isHoveringInfoWindowRef.current = false;
              setShowInfoWindow(false);
            }}
          >
            <div className="space-y-2">
              {strPropertyCard ? (
                <StrPropertyPinCard
                  address={fullAddress}
                  addressUnverified={pin.strCard?.addressUnverified === true}
                  statusLabel={strStatusLabel ?? "Unregistered"}
                  platforms={pin.strCard?.platforms ?? []}
                  heroImageUrl={pin.strCard?.heroImageUrl ?? null}
                />
              ) : null}
              {/* Street View at the top */}
              {!strPropertyCard && streetViewImageUrl && streetViewFullUrl && (
                <button
                  type="button"
                  className="relative w-full h-32 rounded overflow-hidden border p-0 bg-transparent"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(streetViewFullUrl, "_blank");
                  }}
                >
                  <img
                    src={streetViewImageUrl}
                    alt="Street View of property"
                    className="w-full h-full object-cover pointer-events-none"
                  />
                </button>
              )}
              {/* Address and Status side by side */}
              {!strPropertyCard ? (
                <div className="flex gap-4 items-start">
                  {fullAddress && (
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-700">
                        Address
                      </p>
                      <div className="text-sm text-gray-900 space-y-0.5">
                        <div>{fullAddress}</div>
                      </div>
                    </div>
                  )}
                  <div className="flex-shrink-0">
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-semibold text-gray-700">
                        Status
                      </p>
                      {showRegistrationStatusFlowInfo && (
                        <RegistrationStatusInfoButton />
                      )}
                    </div>
                    <Badge variant={statusInfo.variant} className="text-sm">
                      {statusInfo.label}
                    </Badge>
                  </div>
                </div>
              ) : null}
            </div>
          </button>
        </InfoWindow>
      )}
    </>
  );
}

function RegionOutlines({
  jurisdictionIds,
  selectedRegionIds,
}: {
  jurisdictionIds: string[];
  selectedRegionIds: string[];
}) {
  const map = useMap();
  const { data } = useQuery({
    queryKey: ["dashboard-region-outlines", jurisdictionIds],
    queryFn: () => listJurisdictionRegions(jurisdictionIds),
    enabled: jurisdictionIds.length > 0,
  });
  const selected = React.useMemo(
    () => new Set(selectedRegionIds),
    [selectedRegionIds],
  );

  React.useEffect(() => {
    if (!map || !data) return;
    const visible = data.regions.filter((region) => region.enabled !== false);
    const emphasizedGroups =
      selected.size === 0
        ? []
        : visible
            .filter((region) => selected.has(region.id))
            .flatMap((region) => polygonGroupsFromGeometry(region.geometry));
    const clearMask =
      emphasizedGroups.length > 0
        ? attachOutsideMask(map, emphasizedGroups)
        : null;
    const overlays = visible.flatMap((region) => {
      const rings = regionLatLngRings(region.geometry);
      if (rings.length === 0) return [];
      const emphasized = selected.size > 0 && selected.has(region.id);
      return [
        new google.maps.Polygon({
          paths: rings,
          map,
          clickable: false,
          strokeColor: emphasized ? "#0f766e" : "#94a3b8",
          strokeWeight: emphasized ? 2 : 1,
          fillColor: "#0f766e",
          fillOpacity: emphasized || selected.size > 0 ? 0 : 0.04,
        }),
      ];
    });
    return () => {
      clearMask?.();
      for (const overlay of overlays) overlay.setMap(null);
    };
  }, [map, data, selected]);

  return null;
}

export function DashboardMap({
  pins,
  clusters = [],
  totalInView,
  onPinClick,
  onViewportChange,
  isLoading = false,
  className,
  showRegistrationStatusFlowInfo = true,
  regionJurisdictionIds = [],
  selectedRegionIds = [],
  regionOverlay,
  infoCard = "registration",
}: DashboardMapProps) {
  const serverMode = onViewportChange != null;
  const [localSummary, setLocalSummary] =
    React.useState<MapClusterSummaryState | null>(null);
  const handleSummary = React.useCallback((summary: MapClusterSummaryState) => {
    setLocalSummary(summary);
  }, []);
  const summary: MapClusterSummaryState = serverMode
    ? {
        mode: clusters.length > 0 ? "clusters" : "pins",
        totalInView: totalInView ?? pins.length,
        clusterCount: clusters.length,
      }
    : (localSummary ?? {
        mode: "pins",
        totalInView: pins.length,
        clusterCount: 0,
      });
  const renderPin = React.useCallback(
    (pin: DashboardMapPin) => (
      <LicenseMarker
        key={pin.id}
        pin={pin}
        onPinClick={onPinClick}
        showRegistrationStatusFlowInfo={showRegistrationStatusFlowInfo}
        infoCard={infoCard}
      />
    ),
    [infoCard, onPinClick, showRegistrationStatusFlowInfo],
  );

  return (
    <div
      className={cn("relative w-full rounded-lg overflow-hidden", className)}
    >
      <MapZoomHint />
      <MapClusterSummary
        mode={summary.mode}
        totalInView={summary.totalInView}
        clusterCount={summary.clusterCount}
      />
      <GoogleMap
        mapId="dashboard-map"
        defaultCenter={{ lat: 39.8283, lng: -98.5795 }}
        defaultZoom={4}
        gestureHandling={MAP_ZOOM_GESTURE_HANDLING}
        disableDefaultUI={false}
      >
        {regionOverlay ? (
          <DashboardMapRegionOverlay
            selectedRegionIds={selectedRegionIds}
            selectedJurisdictionIds={regionOverlay.selectedJurisdictionIds}
            portalJurisdictionIds={regionOverlay.portalJurisdictionIds}
            onRegionFilterChange={regionOverlay.onRegionFilterChange}
          />
        ) : (
          <RegionOutlines
            jurisdictionIds={regionJurisdictionIds}
            selectedRegionIds={selectedRegionIds}
          />
        )}
        {serverMode && onViewportChange ? (
          <ServerClusteredMarkers
            pins={pins}
            clusters={clusters}
            renderPoint={renderPin}
            onViewportChange={onViewportChange}
            isLoading={isLoading}
          />
        ) : (
          <LocalClusteredMarkers
            points={pins}
            renderPoint={renderPin}
            onSummary={handleSummary}
            isLoading={isLoading}
          />
        )}
      </GoogleMap>
    </div>
  );
}
